import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Video, Square, RotateCcw, Play, Pause, Check } from 'lucide-react';

const STATES = { idle: 'idle', countdown: 'countdown', recording: 'recording', preview: 'preview' };

export default function VideoRecorder({ maxSeconds = 30, onRecordingComplete, teleprompterText = '' }) {
    const [state, setState] = useState(STATES.idle);
    const [countdownVal, setCountdownVal] = useState(3);
    const [elapsed, setElapsed] = useState(0);
    const [videoUrl, setVideoUrl] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [error, setError] = useState(null);

    const videoRef = useRef(null);
    const previewVideoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const countdownRef = useRef(null);

    /* Start camera */
    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: true,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
            setError(null);
        } catch (err) {
            setError('Camera access denied. Please allow camera and microphone access.');
        }
    }, []);

    /* Stop camera */
    const stopCamera = useCallback(() => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
    }, []);

    useEffect(() => { return () => { stopCamera(); clearInterval(timerRef.current); clearInterval(countdownRef.current); }; }, [stopCamera]);

    /* Begin countdown → record */
    const startCountdown = useCallback(async () => {
        if (!streamRef.current) await startCamera();
        if (!streamRef.current) return;
        setState(STATES.countdown);
        setCountdownVal(3);
        let c = 3;
        countdownRef.current = setInterval(() => {
            c--;
            if (c <= 0) {
                clearInterval(countdownRef.current);
                beginRecording();
            } else {
                setCountdownVal(c);
            }
        }, 1000);
    }, [startCamera]);

    const beginRecording = useCallback(() => {
        chunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
            ? 'video/webm;codecs=vp9,opus'
            : 'video/webm';
        const recorder = new MediaRecorder(streamRef.current, { mimeType });
        recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mimeType });
            const url = URL.createObjectURL(blob);
            setVideoUrl(url);
            setState(STATES.preview);
            stopCamera();
            onRecordingComplete?.(blob);
        };
        recorderRef.current = recorder;
        recorder.start(1000);
        setState(STATES.recording);
        setElapsed(0);
        timerRef.current = setInterval(() => {
            setElapsed(prev => {
                if (prev + 1 >= maxSeconds) {
                    recorder.stop();
                    clearInterval(timerRef.current);
                    return maxSeconds;
                }
                return prev + 1;
            });
        }, 1000);
    }, [maxSeconds, onRecordingComplete, stopCamera]);

    const stopRecording = useCallback(() => {
        clearInterval(timerRef.current);
        recorderRef.current?.stop();
    }, []);

    const reRecord = useCallback(async () => {
        if (videoUrl) URL.revokeObjectURL(videoUrl);
        setVideoUrl(null);
        setElapsed(0);
        setState(STATES.idle);
        await startCamera();
    }, [videoUrl, startCamera]);

    const togglePlayback = () => {
        if (!previewVideoRef.current) return;
        if (playing) { previewVideoRef.current.pause(); }
        else { previewVideoRef.current.play(); }
        setPlaying(!playing);
    };

    const pct = maxSeconds > 0 ? (elapsed / maxSeconds) * 100 : 0;

    if (error) {
        return (
            <div className="bg-stone-900 rounded-xl aspect-video flex items-center justify-center p-6">
                <div className="text-center">
                    <Video className="w-10 h-10 text-white/40 mx-auto mb-3" />
                    <p className="text-white/70 text-sm mb-3">{error}</p>
                    <button onClick={startCamera}
                        className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative rounded-xl overflow-hidden bg-stone-900">
            {/* Live camera / countdown / recording */}
            {state !== STATES.preview && (
                <div className="relative aspect-video">
                    <video ref={videoRef} muted playsInline className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />

                    {/* Countdown overlay */}
                    {state === STATES.countdown && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-7xl font-bold text-white animate-pulse">{countdownVal}</span>
                        </div>
                    )}

                    {/* Recording indicator + timer */}
                    {state === STATES.recording && (
                        <>
                            <div className="absolute top-3 left-3 flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                                <span className="text-white text-sm font-mono">
                                    {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
                                </span>
                            </div>
                            {/* Progress bar */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                                <div className="h-full bg-red-500 transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
                            </div>
                        </>
                    )}

                    {/* Teleprompter */}
                    {state === STATES.recording && teleprompterText && (
                        <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-3">
                            <p className="text-white/90 text-sm leading-relaxed">{teleprompterText}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Preview */}
            {state === STATES.preview && videoUrl && (
                <div className="relative aspect-video cursor-pointer" onClick={togglePlayback}>
                    <video ref={previewVideoRef} src={videoUrl} playsInline
                        onEnded={() => setPlaying(false)}
                        className="w-full h-full object-cover" />
                    {!playing && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                                <Play className="w-6 h-6 text-stone-900 ml-1" />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Controls */}
            <div className="p-3 flex items-center justify-center gap-3">
                {state === STATES.idle && (
                    <button onClick={startCountdown}
                        className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors">
                        <Video className="w-4 h-4" /> Start Recording
                    </button>
                )}
                {state === STATES.recording && (
                    <button onClick={stopRecording}
                        className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors">
                        <Square className="w-4 h-4" /> Stop
                    </button>
                )}
                {state === STATES.preview && (
                    <>
                        <button onClick={reRecord}
                            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm hover:bg-white/20 transition-colors">
                            <RotateCcw className="w-4 h-4" /> Re-record
                        </button>
                        <button onClick={() => {}}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full text-sm font-medium hover:bg-green-600 transition-colors">
                            <Check className="w-4 h-4" /> Use This
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
